import React, {useCallback, useRef, useState} from 'react';
import {Handle, NodeProps, Position} from 'reactflow';
import {useSelector} from 'react-redux';
import {useTranslation} from 'react-i18next';
import {RuleResultType} from '../../../../../incase-domain/appliedRules';
import {selectedPresentationSlidesSelector} from '../../../../../store/selectors/presentation.selectors';
import {ActionNodeData, RULE_TYPE_MODIFIER} from '../nodes.types';

const RULE_TYPE_CYCLE: RuleResultType[] = [RuleResultType.JUMP_TO_SLIDE, RuleResultType.HIDE_SLIDES, RuleResultType.SHOW_SLIDES];

function ActionNode({data}: NodeProps<ActionNodeData>): JSX.Element {
    const {t} = useTranslation();
    const allSlides = useSelector(selectedPresentationSlidesSelector);

    const [pickerOpen, setPickerOpen] = useState(false);
    const pickerRef = useRef<HTMLDivElement>(null);

    const handleCycleType = useCallback(() => {
        const currentIdx = RULE_TYPE_CYCLE.indexOf(data.ruleType);
        const nextIdx = (currentIdx + 1) % RULE_TYPE_CYCLE.length;
        data.onChangeType(RULE_TYPE_CYCLE[nextIdx]);
    }, [data]);

    const handleAddSlide = useCallback(
        (slideId: string) => {
            data.onAddSlide(slideId);
        },
        [data]
    );

    const availableSlides = allSlides.filter((s) => !data.slideIds.includes(s._id));

    const modifier = RULE_TYPE_MODIFIER[data.ruleType] ?? 'jump';

    return (
        <div className={`rf-action-node rf-action-node--${modifier}${pickerOpen ? ' rf-action-node--picker-open' : ''}`}>
            <Handle type="target" position={Position.Left} id="action-incoming" className="rf-action-node__handle-in" />

            <Handle type="source" position={Position.Right} id="action-ghost-out" className="rf-action-node__handle-out" />

            <div className="rf-action-node__badge nodrag" onClick={handleCycleType} title={t('presEditPage.rules.clickToCycleType')}>
                {t(`presEditPage.rules.ruleTypeLabels.${data.ruleType}`)}
            </div>

            <div className="rf-action-node__slides">
                {data.slideIds.length === 0 && <span className="rf-action-node__empty">{t('presEditPage.rules.noSlidesSelected')}</span>}
                {data.slideIds.map((slideId) => {
                    const slide = allSlides.find((s) => s._id === slideId);
                    return (
                        <div key={slideId} className="rf-action-node__chip nodrag">
                            <span className="rf-action-node__chip-name" title={slide?.name ?? slideId}>
                                {slide?.name ?? slideId}
                            </span>
                            <button
                                className="rf-action-node__chip-remove"
                                onClick={() => data.onRemoveSlide(slideId)}
                                aria-label="Remove slide"
                            >
                                ×
                            </button>
                        </div>
                    );
                })}

                <div className="rf-action-node__picker-wrapper nodrag" ref={pickerRef}>
                    <button className="rf-action-node__add-btn" onClick={() => setPickerOpen((o) => !o)}>
                        + {t('presEditPage.rules.addSlide')}
                    </button>
                    {pickerOpen && (
                        <div className="rf-action-node__picker">
                            {availableSlides.length === 0 ? (
                                <div className="rf-action-node__picker-empty">{t('presEditPage.rules.noMoreSlides')}</div>
                            ) : (
                                availableSlides.map((slide) => (
                                    <button
                                        key={slide._id}
                                        className="rf-action-node__picker-item"
                                        onClick={() => handleAddSlide(slide._id)}
                                    >
                                        {slide.name}
                                    </button>
                                ))
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default ActionNode;
